import { designVariables } from "@studio3t/design-variables";
import styled from "styled-components";

const SEMANTIC = designVariables.ui.semantic;
const FORM = designVariables.ui.form;
const INPUT = designVariables.ui.input;
const CARDS = designVariables.ui.cards;
const HEADING = designVariables.type.heading;
const SINGLE_LINE = designVariables.type.singleLine;
const PARAGRAPH = designVariables.type.paragraph;

const SPACING_XL = SEMANTIC.spacingxl;

// Raw values with no design-variable counterpart: the centered column width
// (the 780px card of the current mocks, e.g. Figma 16274-166328), the
// common-fields width inside it (the same 30px inset each side as Figma
// 13017-8763), and how far the common-fields block overhangs the card's top
// border.
const COLUMN_WIDTH = 780;
const COMMON_FIELDS_WIDTH = 720;
const CARD_OVERLAP = 16;

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${SEMANTIC.spacingl}px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
`;

// Centered column holding the common fields block and the provider card,
// per Figma node 14055-94855. The form element doubles as the column so the
// header's Add button can requestSubmit every field.
export const Form = styled.form`
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${COLUMN_WIDTH}px;
  max-width: 100%;
  /* Figma puts spacingXL between the title row and the form; the shared
     sticky ListPageHeader already carries spacingS of it as padding. */
  margin-top: ${SPACING_XL - SEMANTIC.spacings}px;
`;

// Service name + Provider live above the card and overhang its top border;
// the app-background fill masks the border behind them (Figma 14055-94717).
// position/z-index keep the block painting above the card it overlaps.
export const CommonFields = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapgroup}px;
  width: ${COMMON_FIELDS_WIDTH}px;
  max-width: 100%;
  margin-bottom: -${CARD_OVERLAP}px;
  padding: 0 ${SEMANTIC.spacings}px;
  background: ${(props) => props.theme.semanticAppBackground};
`;

// Provider-specific form card per Figma node 13017-8765. The extra top
// padding is the Figma first-section inset (spacingL) that clears the
// overhanging common fields block.
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapsection}px;
  width: 100%;
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: ${CARDS.corner}px;
  padding: ${SPACING_XL + SEMANTIC.spacingl}px ${SPACING_XL}px ${SPACING_XL}px;
`;

export const FormSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapgroup}px;
`;

export const Label = styled.label`
  display: inline-flex;
  align-items: center;
  min-height: ${INPUT.size}px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  color: ${(props) => props.theme.inputLabel};
`;

// Label + input + supportive stack. The flex gap is the input→supportive
// distance; the label carries extra bottom margin to reach the larger
// label→input gap from the same flat structure.
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;

  > ${Label} {
    margin-bottom: ${INPUT.gaplabel - INPUT.gapsupportive}px;
  }
`;

// Inline label + control row (Provider, Write mode, Auth mode). The label is
// centered against the 32px control even when supportive text sits below it.
export const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${INPUT.gaplabel}px;

  > ${Label} {
    height: ${INPUT.height}px;
  }
`;

export const FieldRowControl = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;
`;

// Control column of a FieldRow that also hosts a dependent row beneath its
// own control (Change capture's Table under PostgreSQL service, Figma
// 16309-26993): the dependent row's label starts where the parent control
// does, a gapitems below the parent's supportive text.
export const FieldRowStack = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapitems}px;
`;

// Two stacked-label groups sharing one row (Database name + Write mode,
// Figma 14055-95222): the first group stretches, the aside keeps the fixed
// Figma width.
export const FieldPairRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${FORM.gapitems}px;

  > ${FormGroup}:first-child {
    flex: 1;
    min-width: 0;
  }
`;

// The aside is 200px in most mocks; PostgreSQL's Write mode (Figma
// 16274-166334) widens it to 290px.
export const FieldPairAside = styled(FormGroup)<{ $width?: number }>`
  flex: 0 0 ${(props) => props.$width ?? 200}px;
`;

// Equal-width stacked-label groups sharing one row (Max retries + timeouts,
// Figma 14055-95579).
export const FieldRowEven = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${FORM.gapitems}px;

  > ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

// Underline-style text input per Figma "TextInput" (10651-14631).
export const Input = styled.input`
  height: ${INPUT.height}px;
  padding: 0 ${INPUT.paddingw}px;
  border: none;
  border-bottom: 1px solid ${(props) => props.theme.inputDefaultBorder};
  background: transparent;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.default.regular.fontWeight};
  line-height: 1.2;
  color: ${(props) => props.theme.inputPristineText};

  &:hover {
    border-bottom-color: ${(props) => props.theme.inputHoverBorder};
  }

  &:focus {
    outline: none;
    border-bottom-color: ${(props) => props.theme.inputActiveBorder};
  }

  &::placeholder {
    color: ${(props) => props.theme.semanticTextPlaceholder};
  }

  /* Disabled state per Figma 14057-46793: subtle grey fill, body-colored
     value so the fixed content stays readable. */
  &:disabled {
    background: ${(props) => props.theme.primitivesNeutralsAlphaB5};
    border-bottom-color: ${(props) => props.theme.inputDisabledBorder};
    color: ${(props) => props.theme.semanticTextBody};
  }
`;

// Underline input box with a leading icon (Figma TextInput 10568-54977 with
// its Leading icon slot, e.g. the UDF folder name 14058-10184): the border
// spans icon and text, so the input inside is bare.
export const IconInputBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${INPUT.gapinternal}px;
  height: ${INPUT.height}px;
  padding: 0 ${INPUT.paddingw}px;
  border-bottom: 1px solid ${(props) => props.theme.inputDefaultBorder};

  &:hover {
    border-bottom-color: ${(props) => props.theme.inputHoverBorder};
  }

  &:focus-within {
    border-bottom-color: ${(props) => props.theme.inputActiveBorder};
  }
`;

// Figma "Leading icon" 323-12119 is 16px, with no input-level variable.
export const INPUT_LEADING_ICON_SIZE = 16;

export const BareInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.default.regular.fontWeight};
  line-height: 1.2;
  color: ${(props) => props.theme.inputPristineText};

  &::placeholder {
    color: ${(props) => props.theme.semanticTextPlaceholder};
  }
`;

// Sub-block header without the SectionTitle rule (Figma 14055-95149,
// 14058-10197): H5 title plus a body-size description; the same gap spaces
// the content that follows.
export const Subsection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapitems}px;
  width: 100%;
`;

export const SubsectionTitle = styled.h4`
  margin: 0;
  font-size: ${HEADING.h5.fontSize}px;
  font-weight: ${HEADING.h5.fontWeight};
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextHeading};
`;

export const SubsectionText = styled.p`
  margin: 0;
  font-size: ${PARAGRAPH.default.regular.fontSize}px;
  font-weight: ${PARAGRAPH.default.regular.fontWeight};
  line-height: 1.4;
  color: ${(props) => props.theme.semanticTextBody};
`;

// Upload panel per Figma 14055-95150 (Pub/Sub key, reused for PEM fields):
// subtle grey card holding an upload button row and a paste textarea,
// separated by a full-width rule. Vertical padding lives on the panel; the
// rows carry the horizontal inset so the divider can span edge to edge.
export const UploadPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacingl}px;
  width: 100%;
  padding: ${SEMANTIC.spacingl}px 0;
  background: ${(props) => props.theme.neutrals10};
  border: 1px solid ${(props) => props.theme.semanticBorderLight};
  border-radius: ${CARDS.corner}px;
`;

// Grey inset panel grouping related fields on one row (the scanner's From/To
// bounds, Figma 16099-154176): same fill and rule as the upload panel, with
// the padding on all four sides and the item gap between the fields.
export const InsetPanelRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${FORM.gapitems}px;
  width: 100%;
  padding: ${SEMANTIC.spacingl}px;
  background: ${(props) => props.theme.neutrals10};
  border: 1px solid ${(props) => props.theme.semanticBorderLight};
  border-radius: ${CARDS.corner}px;
`;

export const UploadPanelRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;
  padding: 0 ${SEMANTIC.spacingl}px;
`;

export const UploadPanelDivider = styled.div`
  height: 1px;
  background: ${(props) => props.theme.semanticBorderLight};
`;

// Upload button + chosen-file label, mirroring a native file input's
// "No file chosen" affordance. The label absorbs the leftover row width so a
// long filename truncates with an ellipsis instead of widening the panel.
export const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  min-width: 0;
`;

export const UploadFileName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${PARAGRAPH.default.regular.fontSize}px;
  line-height: 1.4;
  color: ${(props) => props.theme.semanticTextLight};
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

// Underline-only textarea per Figma "Textarea" (14055-95153) — blends into
// the upload panel background instead of drawing its own box.
export const PanelTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  max-height: 360px;
  padding: ${INPUT.padding}px;
  border: none;
  border-bottom: 1px solid ${(props) => props.theme.inputDefaultBorder};
  background: transparent;
  font-family: inherit;
  font-size: ${PARAGRAPH.default.regular.fontSize}px;
  line-height: 1.4;
  color: ${(props) => props.theme.inputPristineText};
  resize: vertical;

  &:focus {
    outline: none;
    border-bottom-color: ${(props) => props.theme.inputActiveBorder};
  }

  &::placeholder {
    color: ${(props) => props.theme.semanticTextPlaceholder};
  }
`;

// Figma binds the supportive text colour to Input/textLabel (10651-14568).
// The Add button row closing an editable list (HTTP Source routes, the
// generator's field rules): the same top padding wherever a list has one.
export const ListAddRow = styled.div`
  padding-top: ${FORM.gapitems}px;
`;

export const HelpText = styled.span`
  font-size: ${PARAGRAPH.small.regular.fontSize}px;
  font-weight: ${PARAGRAPH.small.regular.fontWeight};
  line-height: 1.4;
  color: ${(props) => props.theme.inputLabel};
`;

// Section heading with a trailing rule per Figma "Form/header" (13017-8774).
export const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  margin: 0;
  font-size: ${HEADING.h5.fontSize}px;
  font-weight: ${HEADING.h5.fontWeight};
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextHeading};

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(props) => props.theme.semanticBorderLight};
  }
`;

// Section heading with a body-size description under it per Figma
// "Form/header" 16274-166330: the ruled title, then the intro paragraph a
// gapitems apart. The paragraph shares the Subsection description style.
export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapitems}px;
  width: 100%;
`;

export const SectionText = SubsectionText;

// LLM card head per Figma 16029-234479: the AI provider row, then the
// default-model checkbox block, a form group apart.
export const LlmSetup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapgroup}px;
`;

// Default-model checkbox + its info alert (Figma 16029-234419).
export const DefaultModelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacingxs}px;
`;

// Checkbox + label + supportive (Figma Checkbox 13510-51539): the library
// checkbox spaces its label by gapinternal, the design by gaplabel.
export const CheckboxBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;

  > label {
    gap: ${INPUT.gaplabel}px;
  }
`;

// Supportive line under a checkbox label, indented past the box.
export const CheckboxHelpText = styled(HelpText)<{ $disabled?: boolean }>`
  padding-left: ${INPUT.size + INPUT.gaplabel}px;
  color: ${(props) =>
    props.$disabled
      ? props.theme.semanticTextDisabled
      : props.theme.inputLabel};
`;
